/**
 * Settings load/save/auto-save controller (keeps SettingsView render CC low).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { settingsClient } from '../../services/clients';
import type {
  AiGenerationSettings,
  JiraConnectionSettings,
  SlackVoiceSettings,
  UserSettingsPublic,
  UserSettingsUpdate,
} from '../../services/clients/types';
import {
  aiChanged,
  aiSavePatch,
  defaultAiDraft,
  defaultJiraDraft,
  defaultVoiceDraft,
  errorMessage,
  jiraConnectionEqual,
  nextVoiceDraft,
  shouldForceNotesOnPatch,
  voiceSelectsEqual,
} from './settingsHelpers';

export function useSettingsController() {
  const [settings, setSettings] = useState<UserSettingsPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const [slackTokenDraft, setSlackTokenDraft] = useState('');
  const [githubTokenDraft, setGithubTokenDraft] = useState('');
  const [jiraTokenDraft, setJiraTokenDraft] = useState('');
  const [aiDraft, setAiDraft] = useState<AiGenerationSettings>(defaultAiDraft);
  const [voiceDraft, setVoiceDraft] = useState<SlackVoiceSettings>(defaultVoiceDraft);
  const [jiraDraft, setJiraDraft] = useState<JiraConnectionSettings>(defaultJiraDraft);
  const [savedVoicePrompt, setSavedVoicePrompt] = useState('');

  const savedHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsRef = useRef<UserSettingsPublic | null>(null);
  const voiceDraftRef = useRef(voiceDraft);
  const savedVoicePromptRef = useRef(savedVoicePrompt);
  const pendingAiRef = useRef<AiGenerationSettings | null>(null);
  const pendingVoiceRef = useRef<SlackVoiceSettings | null>(null);
  const pendingJiraRef = useRef<JiraConnectionSettings | null>(null);
  const autoSaveBusyRef = useRef(false);
  const mountedRef = useRef(true);

  voiceDraftRef.current = voiceDraft;
  savedVoicePromptRef.current = savedVoicePrompt;

  const flashSaved = useCallback((label: string) => {
    setSavedHint(label);
    if (savedHintTimer.current) clearTimeout(savedHintTimer.current);
    savedHintTimer.current = setTimeout(() => setSavedHint(null), 1800);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (savedHintTimer.current) clearTimeout(savedHintTimer.current);
    };
  }, []);

  const applyPublic = (data: UserSettingsPublic, opts?: { forceNotes?: boolean }) => {
    settingsRef.current = data;
    setSettings(data);
    setAiDraft({ ...data.ai });
    setJiraDraft({ ...data.jira });
    const { voice, keepNotes } = nextVoiceDraft(
      data,
      voiceDraftRef.current.customPrompt,
      savedVoicePromptRef.current,
      opts?.forceNotes,
    );
    voiceDraftRef.current = voice;
    setVoiceDraft(voice);
    if (!keepNotes) {
      savedVoicePromptRef.current = data.slackVoice.customPrompt;
      setSavedVoicePrompt(data.slackVoice.customPrompt);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const envelope = await settingsClient.getSettings();
      if (!mountedRef.current) return;
      applyPublic(envelope.data, { forceNotes: true });
    } catch (err) {
      if (!mountedRef.current) return;
      setError(errorMessage(err, 'No se pudieron cargar los ajustes'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const savePatch = async (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
    opts?: { quiet?: boolean; forceNotes?: boolean },
  ) => {
    setSaving(true);
    setError(null);
    if (!opts?.quiet) setStatusMessage(null);
    try {
      const envelope = await settingsClient.updateSettings(patch);
      // A save can outlive the component: closing the modal flushes a pending
      // system-prompt draft. Applying its result to a torn-down tree is a
      // React warning at best and a stale-state write at worst.
      if (!mountedRef.current) return;
      const forceNotes = shouldForceNotesOnPatch(
        opts?.forceNotes,
        Boolean(
          patch.slackVoice &&
          Object.prototype.hasOwnProperty.call(patch.slackVoice, 'customPrompt'),
        ),
      );
      applyPublic(envelope.data, { forceNotes });
      after?.();
      if (opts?.quiet) flashSaved(successMessage);
      else setStatusMessage(successMessage);
    } catch (err) {
      // The user can no longer see a banner for a request that outlived the
      // screen, so at least leave the failure in the console.
      if (!mountedRef.current) {
        console.error('[settings] save failed after unmount:', err);
        return;
      }
      setError(errorMessage(err, 'No se pudieron guardar los ajustes'));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const flushOneAutoSave = async () => {
    const nextAi = pendingAiRef.current;
    pendingAiRef.current = null;
    const nextVoice = pendingVoiceRef.current;
    pendingVoiceRef.current = null;
    const nextJira = pendingJiraRef.current;
    pendingJiraRef.current = null;
    const current = settingsRef.current;
    if (nextAi && current && aiChanged(nextAi, current.ai)) {
      await savePatch({ ai: aiSavePatch(nextAi, current.ai) }, 'Guardado', undefined, {
        quiet: true,
      });
    }
    if (nextVoice && current && !voiceSelectsEqual(nextVoice, current.slackVoice)) {
      await savePatch(
        {
          slackVoice: {
            language: nextVoice.language,
            tone: nextVoice.tone,
            replyLength: nextVoice.replyLength,
          },
        },
        'Guardado',
        undefined,
        { quiet: true },
      );
    }
    if (nextJira && current && !jiraConnectionEqual(nextJira, current.jira)) {
      await savePatch(
        {
          jira: {
            baseUrl: nextJira.baseUrl.trim().replace(/\/+$/, ''),
            email: nextJira.email.trim(),
          },
        },
        'Guardado',
        undefined,
        { quiet: true },
      );
    }
  };

  const flushAutoSaves = async () => {
    if (autoSaveBusyRef.current) return;
    autoSaveBusyRef.current = true;
    try {
      while (pendingAiRef.current || pendingVoiceRef.current || pendingJiraRef.current) {
        await flushOneAutoSave();
      }
    } finally {
      autoSaveBusyRef.current = false;
      if (pendingAiRef.current || pendingVoiceRef.current || pendingJiraRef.current) {
        void flushAutoSaves();
      }
    }
  };

  const autoSaveAi = (next: AiGenerationSettings) => {
    pendingAiRef.current = next;
    void flushAutoSaves();
  };

  const autoSaveVoiceSelects = (next: SlackVoiceSettings) => {
    pendingVoiceRef.current = next;
    void flushAutoSaves();
  };

  const autoSaveJiraConnection = (next: JiraConnectionSettings) => {
    pendingJiraRef.current = next;
    void flushAutoSaves();
  };

  const cancelNotes = () => {
    const restored = savedVoicePromptRef.current;
    setVoiceDraft((prev) => {
      const next = { ...prev, customPrompt: restored };
      voiceDraftRef.current = next;
      return next;
    });
  };

  const setVoiceDraftSynced = (next: SlackVoiceSettings) => {
    voiceDraftRef.current = next;
    setVoiceDraft(next);
  };

  return {
    settings,
    loading,
    saving,
    error,
    statusMessage,
    savedHint,
    slackTokenDraft,
    githubTokenDraft,
    jiraTokenDraft,
    aiDraft,
    voiceDraft,
    jiraDraft,
    savedVoicePrompt,
    setSlackTokenDraft,
    setGithubTokenDraft,
    setJiraTokenDraft,
    setAiDraft,
    setJiraDraft,
    setVoiceDraftSynced,
    load,
    savePatch,
    applyExternalSettings: (data: UserSettingsPublic) => applyPublic(data, { forceNotes: true }),
    autoSaveAi,
    autoSaveVoiceSelects,
    autoSaveJiraConnection,
    cancelNotes,
  };
}
